// src/pages/ModuleManagement/index.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import NumberInput from 'Components/Common/NumberInput';
import { useBrandingTitle } from '../../hooks';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import {
  fetchModules,
  createNewModule,
  updateExistingModule,
  deleteModuleById,
} from 'slices/moduleManagement/thunk';
import {
  openModal,
  closeModal,
  setSelectedModule,
} from 'slices/moduleManagement/reducer';
import { CreateModuleDto, ModuleItem, ModuleTreeItem, UpdateModuleDto } from './types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('module_management');

const initialCreateForm: CreateModuleDto = {
  code: '',
  name: '',
  description: '',
  icon: '',
  url: '',
  parentId: null,
  displayOrder: 0,
  isActive: true,
  isShowMenu: true,
};

type CreateModuleErrors = Partial<Record<'code' | 'name' | 'url', string>>;

// Validate FE trước khi gọi API — khớp rule bắt buộc của CreateModuleCommand (Code/Name required, Code
// duy nhất — check trùng vẫn để backend trả về vì cần query DB), cộng thêm vài rule hợp lý phía FE
// (không khoảng trắng/ký tự lạ trong Code, Url phải là đường dẫn tương đối bắt đầu bằng "/").
const validateCreateForm = (form: CreateModuleDto): CreateModuleErrors => {
  const errors: CreateModuleErrors = {};

  const code = form.code.trim();
  if (!code) {
    errors.code = st('vui_long_nhap_ma_danh_muc_text');
  } else if (code.length < 2) {
    errors.code = st('ma_danh_muc_phai_co_it_text');
  } else if (!/^[A-Za-z0-9_-]+$/.test(code)) {
    errors.code = st('ma_danh_muc_chi_duoc_chua_text');
  }

  const name = form.name.trim();
  if (!name) {
    errors.name = st('vui_long_nhap_ten_danh_muc_text');
  } else if (name.length < 2) {
    errors.name = st('ten_danh_muc_phai_co_it_text');
  } else if (name.length > 200) {
    errors.name = st('ten_danh_muc_khong_duoc_qua_text');
  }

  const url = (form.url || '').trim();
  if (url && !url.startsWith('/')) {
    errors.url = st('duong_dan_fe_phai_bat_dau_text');
  }

  return errors;
};

// Dựng cây cha/con từ danh sách phẳng, sắp xếp theo DisplayOrder ở mỗi cấp
const buildModuleTree = (modules: ModuleItem[]): ModuleTreeItem[] => {
  const nodeMap = new Map<number, ModuleTreeItem>();
  modules.forEach(m => nodeMap.set(m.id, { ...m, children: [] }));

  const roots: ModuleTreeItem[] = [];
  nodeMap.forEach(node => {
    if (node.parentId && nodeMap.has(node.parentId)) {
      nodeMap.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortRecursive = (nodes: ModuleTreeItem[]) => {
    nodes.sort((a, b) => a.displayOrder - b.displayOrder);
    nodes.forEach(n => sortRecursive(n.children));
  };
  sortRecursive(roots);

  return roots;
};

// Làm phẳng cây thành danh sách có depth để render bảng với thụt lề
const ModuleManagementPage: React.FC = () => {
  useBrandingTitle(st('quan_ly_danh_muc_text'));

  const dispatch = useDispatch<any>();
  const location = useLocation();
  const {
    modules,
    selectedModule,
    loading,
    submitting,
    modalState,
  } = useSelector((state: any) => state.ModuleManagement);

  const [createForm, setCreateForm] = useState<CreateModuleDto>(initialCreateForm);
  const [createErrors, setCreateErrors] = useState<CreateModuleErrors>({});
  const [editForm, setEditForm] = useState<UpdateModuleDto | null>(null);
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<number>>(new Set());
  const [activeParentModule, setActiveParentModule] = useState<ModuleTreeItem | null>(null);

  const loadModules = () => {
    dispatch(fetchModules());
  };

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moduleTree = useMemo(() => buildModuleTree(modules), [modules]);
  const moduleRows = useMemo(() => {
    const flatten = (nodes: ModuleTreeItem[], depth = 0): Array<{ module: ModuleTreeItem; depth: number }> =>
      nodes.flatMap(node => [
        { module: node, depth },
        ...flatten(node.children, depth + 1),
      ]);

    return flatten(moduleTree);
  }, [moduleTree]);


  // Danh sách module cha khả dụng — loại module đang sửa để tránh tự chọn chính nó làm cha
  const parentOptions = (excludeId?: number) =>
    modules.filter((m: ModuleItem) => m.id !== excludeId);

  const handleOpenCreate = (parentId?: number | null) => {
    setCreateForm({ ...initialCreateForm, parentId: parentId ?? null });
    setCreateErrors({});
    dispatch(openModal('create'));
  };

  const handleOpenEdit = (module: ModuleItem) => {
    dispatch(setSelectedModule(module));
    setEditForm({
      id: module.id,
      code: module.code,
      name: module.name,
      description: module.description || '',
      icon: module.icon || '',
      url: module.url || '',
      parentId: module.parentId ?? null,
      displayOrder: module.displayOrder,
      isActive: module.isActive,
      // Dữ liệu cũ chưa có field (BE chưa migrate) -> coi như đang hiện trên menu.
      isShowMenu: module.isShowMenu !== false,
    });
    dispatch(openModal('edit'));
  };

  const handleOpenDelete = (module: ModuleItem) => {
    dispatch(setSelectedModule(module));
    dispatch(openModal('delete'));
  };

  const handleCloseCreate = () => {
    if (submitting) return;
    dispatch(closeModal('create'));
  };

  const handleCloseEdit = () => {
    if (submitting) return;
    dispatch(closeModal('edit'));
    dispatch(setSelectedModule(null));
    setEditForm(null);
  };

  const handleCloseDelete = () => {
    if (submitting) return;
    dispatch(closeModal('delete'));
    dispatch(setSelectedModule(null));
  };

  const handleCreateSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const errors = validateCreateForm(createForm);
    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const result = await dispatch(createNewModule(createForm));

    if (createNewModule.fulfilled.match(result)) {
      toast.success(st('tao_danh_muc_thanh_cong_message'));
      loadModules();
    } else {
      toast.error((result.payload as string) || st('khong_the_tao_danh_muc_message'));
    }
  };

  const handleEditSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editForm) return;

    if (!editForm.code.trim() || !editForm.name.trim()) {
      toast.error(st('vui_long_nhap_ma_danh_muc_message'));
      return;
    }

    const result = await dispatch(updateExistingModule(editForm));
    if (updateExistingModule.fulfilled.match(result)) {
      toast.success(st('cap_nhat_danh_muc_thanh_cong_message'));
      loadModules();
    } else {
      toast.error((result.payload as string) || st('khong_the_cap_nhat_danh_muc_message'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedModule) return;
    const result = await dispatch(deleteModuleById(selectedModule.id));
    if (deleteModuleById.fulfilled.match(result)) {
      toast.success(st('xoa_danh_muc_thanh_cong_message'));
    } else {
      toast.error((result.payload as string) || st('khong_the_xoa_danh_muc_message'));
    }
  };

  const handleSelectParent = (module: ModuleTreeItem) => {
    setActiveParentModule(module);
  };

  const handleResetParentSelection = () => {
    setActiveParentModule(null);
  };

  const toggleExpanded = (moduleId: number) => {
    setExpandedModuleIds(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const renderModuleNode = (module: ModuleTreeItem, depth = 0): React.ReactNode => {
    const hasChildren = module.children.length > 0;
    const isExpanded = expandedModuleIds.has(module.id);
    const isActiveParent = activeParentModule?.id === module.id;

    return (
      <div key={module.id} className="module-tree-node">
        <div
          className={`d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2 ${isActiveParent ? 'border-success bg-success-subtle' : 'bg-white'}`}
          style={{ marginLeft: depth * 28, cursor: 'pointer' }}
          title={st('chon_lam_menu_cha_mac_dinh_title')}
          onClick={() => handleSelectParent(module)}
        >
          <div
            className="d-flex align-items-center min-w-0 flex-grow-1"
            role={hasChildren ? 'button' : undefined}
            tabIndex={hasChildren ? 0 : undefined}
            style={{ cursor: hasChildren ? 'pointer' : 'default' }}
            onClick={() => hasChildren && toggleExpanded(module.id)}
            onKeyDown={event => {
              if (!hasChildren) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleExpanded(module.id);
              }
            }}
          >
            <Button
              color="light"
              size="sm"
              className="btn-icon flex-shrink-0 me-2"
              disabled={!hasChildren}
              title={hasChildren ? (isExpanded ? st('thu_gon_text') : st('mo_rong_text')) : st('khong_co_menu_con_text')}
              onClick={event => {
                event.stopPropagation();
                handleSelectParent(module);
                if (hasChildren) {
                  toggleExpanded(module.id);
                }
              }}
            >
              <i className={hasChildren ? (isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line') : 'ri-subtract-line'}></i>
            </Button>

            <i className={`${hasChildren ? 'ri-folder-3-line text-warning' : 'ri-file-list-3-line text-muted'} fs-4 me-2 flex-shrink-0`}></i>

            <div className="min-w-0">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold text-truncate">{module.name}</span>
                <span className="badge bg-light text-muted">{module.code}</span>
              </div>
              <div className="text-muted small text-truncate">
                {module.url || st('khong_co_duong_dan_text')} {st('thu_tu_text')} {module.displayOrder} · {module.permissionCount} {st('quyen_text')}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-3">
            <span className={`badge bg-${module.isActive ? 'success' : 'danger'}-subtle text-${module.isActive ? 'success' : 'danger'}`}>
              {module.isActive ? st('dang_bat_text') : st('da_tat_text')}
            </span>
            {module.isShowMenu === false && (
              <span className="badge bg-secondary-subtle text-secondary" title={st('an_menu_title')}>
                <i className="ri-eye-off-line me-1"></i>{st('an_menu_text')}
              </span>
            )}
            <Button
              color="success"
              outline
              size="sm"
              className="btn-icon"
              title={st('them_menu_con_title')}
              disabled={submitting}
              onClick={event => {
                event.stopPropagation();
                handleOpenCreate(module.id);
              }}
            >
              <i className="ri-menu-add-line"></i>
            </Button>
            {PageActionPermission.canDisplay(location.pathname, "button-edit") && (
              <Button
                color="secondary"
                outline
                size="sm"
                className="btn-icon"
                title={st('sua_title')}
                disabled={submitting}
                data-action-code="button-edit"
                data-action-name="Sửa Danh mục"
                onClick={event => {
                  event.stopPropagation();
                  handleOpenEdit(module);
                }}
              >
                <i className="ri-pencil-line"></i>
              </Button>
            )}
            {PageActionPermission.canDisplay(location.pathname, "button-delete") && (
              <Button
                color="danger"
                outline
                size="sm"
                className="btn-icon"
                title={st('xoa_title')}
                disabled={submitting}
                data-action-code="button-delete"
                data-action-name="Xóa Danh mục"
                onClick={event => {
                  event.stopPropagation();
                  handleOpenDelete(module);
                }}
              >
                <i className="ri-delete-bin-line"></i>
              </Button>
            )}
          </div>
        </div>
        {hasChildren && isExpanded && module.children.map(child => renderModuleNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('quan_ly_danh_muc_text')} pageTitle={st('he_thong_text')} />

          <Card>
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('danh_sach_danh_muc_title')}</h5>
                  <p className="text-muted mb-0">
                    {st('quan_ly_cay_menu_danh_muc_text')}
                  </p>
                  {activeParentModule && (
                    <p className="text-success small mb-0 mt-1">
                      <i className="ri-checkbox-circle-line me-1"></i>
                      {st('dang_chon_text')} <strong>{activeParentModule.name}</strong> {st('lam_menu_cha_khi_them_moi_text')}
                    </p>
                  )}
                </div>
                <div className="col-sm-auto">
                  <div className="hstack gap-2">
                    <Button
                      color="danger"
                      outline={!activeParentModule}
                      disabled={!activeParentModule}
                      onClick={handleResetParentSelection}
                    >
                      <i className="ri-close-circle-line align-bottom me-1"></i>
                      {st('reset_button')}
                    </Button>
                    {PageActionPermission.canDisplay(location.pathname, "button-add") && (
                      <Button
                        color="success"
                        data-action-code="button-add"
                        data-action-name="Thêm Danh mục"
                        onClick={() => handleOpenCreate(activeParentModule?.id ?? null)}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {st('them_danh_muc_button')}
                      </Button>
                    )}
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              {loading ? (
                <div className="placeholder-glow">
                  {Array.from({ length: 4 }).map((_, row) => (
                    <div key={row} className="border rounded px-3 py-3 mb-2">
                      <span className="placeholder col-5"></span>
                      <span className="placeholder col-3 ms-3"></span>
                    </div>
                  ))}
                </div>
              ) : moduleTree.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <div className="mb-2">
                    <i className="ri-folder-3-line fs-1 text-muted"></i>
                  </div>
                  {st('chua_co_danh_muc_nao_text')}
                </div>
              ) : (
                <div className="module-tree">
                  {moduleTree.map(module => renderModuleNode(module))}
                </div>
              )}
              <div className="d-none">
                <table className="table align-middle table-nowrap mb-0">
                  <thead className="table-light text-muted text-uppercase">
                    <tr>
                      <th>{st('ma_danh_muc_column')}</th>
                      <th>{st('ten_danh_muc_column')}</th>
                      <th>{st('icon_column')}</th>
                      <th>{st('duong_dan_column')}</th>
                      <th>{st('thu_tu_column')}</th>
                      <th>{st('so_quyen_column')}</th>
                      <th>{st('trang_thai_column')}</th>
                      <th style={{ width: 140 }}>{st('thao_tac_column')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 3 }).map((_, row) => (
                        <tr key={row}>
                          {Array.from({ length: 8 }).map((__, col) => (
                            <td key={col}>
                              <div className="placeholder-glow">
                                <span className="placeholder col-8"></span>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : moduleRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-5">
                          <div className="mb-2">
                            <i className="ri-menu-line fs-1 text-muted"></i>
                          </div>
                          {st('chua_co_danh_muc_nao_text')}
                        </td>
                      </tr>
                    ) : (
                      moduleRows.map(({ module, depth }) => (
                        <tr key={module.id}>
                          <td className="fw-medium">{module.code}</td>
                          <td>
                            <span style={{ paddingLeft: depth * 24 }}>
                              {depth > 0 && <i className="ri-corner-down-right-line text-muted me-1"></i>}
                              {module.name}
                            </span>
                          </td>
                          <td>
                            {module.icon && <i className={`${module.icon} fs-16`}></i>}
                          </td>
                          <td className="text-muted">{module.url || '—'}</td>
                          <td>{module.displayOrder}</td>
                          <td>{module.permissionCount}</td>
                          <td>
                            <span className={`badge bg-${module.isActive ? 'success' : 'danger'}-subtle text-${module.isActive ? 'success' : 'danger'}`}>
                              {module.isActive ? st('dang_bat_text') : st('da_tat_text')}
                            </span>
                            {module.isShowMenu === false && (
                              <span className="badge bg-secondary-subtle text-secondary ms-1" title={st('an_menu_title')}>
                                <i className="ri-eye-off-line me-1"></i>{st('an_menu_text')}
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <Button
                                color="success"
                                outline
                                size="sm"
                                className="btn-icon"
                                title={st('them_menu_con_title')}
                                disabled={submitting}
                                onClick={() => handleOpenCreate(module.id)}
                              >
                                <i className="ri-menu-add-line"></i>
                              </Button>
                              <Button
                                color="secondary"
                                outline
                                size="sm"
                                className="btn-icon"
                                title={st('sua_title')}
                                disabled={submitting}
                                onClick={() => handleOpenEdit(module)}
                              >
                                <i className="ri-pencil-line"></i>
                              </Button>
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                className="btn-icon"
                                title={st('xoa_title')}
                                disabled={submitting}
                                onClick={() => handleOpenDelete(module)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Modal Tạo module */}
      <Modal isOpen={modalState.create} toggle={handleCloseCreate} centered size="lg">
        <ModalHeader className="bg-light p-3" toggle={handleCloseCreate}>{st('them_danh_muc_button')}</ModalHeader>
        <Form onSubmit={handleCreateSubmit} noValidate>
          <ModalBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('ma_danh_muc_label')}</Label>
                      <Input
                        required
                        placeholder={st('vd_agent_management_placeholder')}
                        value={createForm.code}
                        invalid={!!createErrors.code}
                        onChange={event => {
                          setCreateForm(prev => ({ ...prev, code: event.target.value }));
                          setCreateErrors(prev => ({ ...prev, code: undefined }));
                        }}
                      />
                      {createErrors.code && <FormFeedback type="invalid">{createErrors.code}</FormFeedback>}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('ten_danh_muc_label')}</Label>
                      <Input
                        required
                        placeholder={st('vd_quan_ly_dai_ly_placeholder')}
                        value={createForm.name}
                        invalid={!!createErrors.name}
                        onChange={event => {
                          setCreateForm(prev => ({ ...prev, name: event.target.value }));
                          setCreateErrors(prev => ({ ...prev, name: undefined }));
                        }}
                      />
                      {createErrors.name && <FormFeedback type="invalid">{createErrors.name}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label>{st('mo_ta_label')}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={createForm.description}
                    onChange={event => setCreateForm(prev => ({ ...prev, description: event.target.value }))}
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('icon_class_fontawesome_remix_label')}</Label>
                      <Input
                        placeholder={st('vd_ri_user_star_line_placeholder')}
                        value={createForm.icon}
                        onChange={event => setCreateForm(prev => ({ ...prev, icon: event.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('duong_dan_fe_url_label')}</Label>
                      <Input
                        placeholder={st('vd_agent_management_placeholder_2')}
                        value={createForm.url}
                        invalid={!!createErrors.url}
                        onChange={event => {
                          setCreateForm(prev => ({ ...prev, url: event.target.value }));
                          setCreateErrors(prev => ({ ...prev, url: undefined }));
                        }}
                      />
                      {createErrors.url && <FormFeedback type="invalid">{createErrors.url}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('danh_muc_cha_label')}</Label>
                      <Input
                        type="select"
                        value={createForm.parentId ?? ''}
                        onChange={event => setCreateForm(prev => ({
                          ...prev,
                          parentId: event.target.value ? Number(event.target.value) : null,
                        }))}
                      >
                        <option value="">{st('khong_co_danh_muc_goc_option')}</option>
                        {parentOptions().map((m: ModuleItem) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>{st('thu_tu_hien_thi_label')}</Label>
                      <NumberInput
                        value={createForm.displayOrder}
                        onChange={val => setCreateForm(prev => ({ ...prev, displayOrder: val }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="d-block">{st('trang_thai_column')}</Label>
                      <div className="form-check form-switch mt-2">
                        <Input
                          type="checkbox"
                          role="switch"
                          checked={createForm.isActive}
                          onChange={event => setCreateForm(prev => ({ ...prev, isActive: event.target.checked }))}
                        />
                        <Label check>{st('kich_hoat_label')}</Label>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="d-block">{st('hien_thi_menu_label')}</Label>
                      <div className="form-check form-switch mt-2">
                        <Input
                          type="checkbox"
                          role="switch"
                          checked={createForm.isShowMenu}
                          onChange={event => setCreateForm(prev => ({ ...prev, isShowMenu: event.target.checked }))}
                        />
                        <Label check>{createForm.isShowMenu ? st('hien_tren_menu_text') : st('chi_phan_quyen_text')}</Label>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button color="light" onClick={handleCloseCreate} disabled={submitting}>{st('huy_button')}</Button>
              <Button color="success" type="submit" disabled={submitting}>
                {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                {st('tao_danh_muc_button')}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Modal Sửa module */}
      <Modal isOpen={modalState.edit} toggle={handleCloseEdit} centered size="lg">
        <ModalHeader className="bg-light p-3" toggle={handleCloseEdit}>
          {st('sua_danh_muc_title')} {selectedModule?.code}
        </ModalHeader>

        <ModalBody>
              {editForm && (
                <Form id="editModuleForm" onSubmit={handleEditSubmit} noValidate>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{st('ma_danh_muc_label')}</Label>
                        <Input
                          required
                          value={editForm.code}
                          onChange={event => setEditForm(prev => prev && ({ ...prev, code: event.target.value }))}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{st('ten_danh_muc_label')}</Label>
                        <Input
                          required
                          value={editForm.name}
                          onChange={event => setEditForm(prev => prev && ({ ...prev, name: event.target.value }))}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <FormGroup>
                    <Label>{st('mo_ta_label')}</Label>
                    <Input
                      type="textarea"
                      rows={2}
                      value={editForm.description}
                      onChange={event => setEditForm(prev => prev && ({ ...prev, description: event.target.value }))}
                    />
                  </FormGroup>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{st('icon_fontawesome_remix_class_label')}</Label>
                        <Input
                          value={editForm.icon}
                          onChange={event => setEditForm(prev => prev && ({ ...prev, icon: event.target.value }))}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{st('duong_dan_fe_url_label')}</Label>
                        <Input
                          value={editForm.url}
                          onChange={event => setEditForm(prev => prev && ({ ...prev, url: event.target.value }))}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{st('danh_muc_cha_label')}</Label>
                        <Input
                          type="select"
                          value={editForm.parentId ?? ''}
                          onChange={event => setEditForm(prev => prev && ({
                            ...prev,
                            parentId: event.target.value ? Number(event.target.value) : null,
                          }))}
                        >
                          <option value="">{st('khong_co_danh_muc_goc_option')}</option>
                          {parentOptions(editForm.id).map((m: ModuleItem) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>{st('thu_tu_hien_thi_label')}</Label>
                        <NumberInput
                          value={editForm.displayOrder}
                          onChange={val => setEditForm(prev => prev && ({ ...prev, displayOrder: val }))}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label className="d-block">{st('trang_thai_column')}</Label>
                        <div className="form-check form-switch mt-2">
                          <Input
                            type="checkbox"
                            role="switch"
                            checked={editForm.isActive}
                            onChange={event => setEditForm(prev => prev && ({ ...prev, isActive: event.target.checked }))}
                          />
                          <Label check>{st('kich_hoat_label')}</Label>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label className="d-block">{st('hien_thi_menu_label')}</Label>
                        <div className="form-check form-switch mt-2">
                          <Input
                            type="checkbox"
                            role="switch"
                            checked={editForm.isShowMenu}
                            onChange={event => setEditForm(prev => prev && ({ ...prev, isShowMenu: event.target.checked }))}
                          />
                          <Label check>{editForm.isShowMenu ? st('hien_tren_menu_text') : st('chi_phan_quyen_text')}</Label>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              )}
        </ModalBody>

        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <Button color="light" onClick={handleCloseEdit} disabled={submitting}>{st('huy_button')}</Button>
            <Button color="success" type="submit" form="editModuleForm" disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {st('luu_thay_doi_button')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Xóa module */}
      <Modal isOpen={modalState.delete} toggle={handleCloseDelete} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{st('xac_nhan_xoa_danh_muc_title')}</h4>
              <p className="text-muted mb-3">
                {st('ban_co_chac_muon_xoa_danh_text')} <strong>{selectedModule?.name}</strong>?
              </p>
              {selectedModule && selectedModule.permissionCount > 0 && (
                <div className="alert alert-warning mb-0">
                  <i className="ri-alert-line me-2"></i>
                  {st('danh_muc_nay_co_text')} <strong>{selectedModule.permissionCount} {st('quyen_text')}</strong>{st('xoa_danh_muc_se_xoa_mem_text')}
                </div>
              )}
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button type="button" color="light" className="w-sm" onClick={handleCloseDelete} disabled={submitting}>
              {st('huy_button')}
            </Button>
            <Button type="button" color="danger" className="w-sm" onClick={handleConfirmDelete} disabled={submitting}>
              {st('xoa_danh_muc_button')}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ModuleManagementPage;
